import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsUserKnown, getUser } from '@app/ducks';
import { mapLookbookNavigationItemToNavigationItem } from '@app/logic/navigation';
import { getIsSeller } from '@app/logic/User';
import { compiledPaths } from '@app/paths';
import { useLookbookDetails } from '@app/services/hooks/lookbook/useLookbookDetails';
import { createAbsoluteUrl } from '@app/utils/createAbsoluteUrl';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useIsScreenSize } from '@app/utils/hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useLookbookWishlistContext } from '../lookbook/context/LookbookWishlistContext';
import { Button } from '../various/Button';
import { FavouritesOutline } from '../various/Icons';

import { Header } from './Header';
import { HeaderWrapper } from './HeaderWrapper';
import { LookbookHeaderBanner } from './LookbookHeaderBanner';
interface Props {
  lookbookId: string;
  shouldHideActions?: boolean;
}

export const LookbookHeader = ({ lookbookId, shouldHideActions = false }: Props) => {
  const { t } = useTranslation(['lookbook']);
  const user = useSelector(getUser);
  const isSeller = getIsUserKnown(user) && getIsSeller(user);

  const { wishlist, wishlistId } = useLookbookWishlistContext();
  const isMobile = useIsScreenSize(BREAKPOINTS.MD);
  const { data } = useLookbookDetails({ lookbookId, refetchOnMount: false });
  const navigationItems = React.useMemo(
    () => (data?.navigationItems ?? []).map(mapLookbookNavigationItemToNavigationItem(lookbookId, wishlistId)),
    [data, lookbookId, wishlistId],
  );
  const isLookbookWishlistEnabled = getIsFeatureEnabled('ENABLE_LOOKBOOK_WISHLIST');
  const isWishlistButtonVisible = isLookbookWishlistEnabled && !shouldHideActions;

  const shareLookbookUrl = createAbsoluteUrl(compiledPaths.LOOKBOOK_SINGLE({ lookbookId }));

  return (
    <HeaderWrapper hasBanner>
      <LookbookHeaderBanner name={data?.name} shareLookbookUrl={shareLookbookUrl} shouldHideActions={shouldHideActions} />
      <Header
        shouldShowMenu={isSeller}
        hasHeaderBanner
        navigationItems={navigationItems}
        logoUrl={compiledPaths.LOOKBOOK_SINGLE({ lookbookId })}
        suffix={
          isWishlistButtonVisible && (
            <Button
              as={Link}
              to={compiledPaths.LOOKBOOK_WISHLIST_PAGE({ lookbookId, wishlistId })}
              variant="bordered"
              color="secondary"
              icon={FavouritesOutline}
            >
              {!isMobile && `${t('lookbook:wishlist')} (${wishlist.size})`}
            </Button>
          )
        }
      />
    </HeaderWrapper>
  );
};
